// src/components/NotificationsPage.jsx
"use client";

import { useState, useEffect, useCallback } from "react"
import { useNavigate } from "react-router-dom"
import {
  FaCalendarAlt,
  FaTasks,
  FaClipboardList,
  FaCheck,
  FaTimes,
  FaSpinner,
  FaBell,
  FaTrash,
  FaCheckDouble
} from "react-icons/fa"
import { FiBell, FiCheck, FiX, FiAlertCircle } from "react-icons/fi"
import Layout from "./Layout"

const rawBackendUrl = process.env.REACT_APP_BACKEND_URL || "";
const backend_url = rawBackendUrl.startsWith("http://") || rawBackendUrl.startsWith("https://") ? rawBackendUrl : `https://${rawBackendUrl}`;
const API_URL = `${backend_url}/api`

export default function NotificationsPage() {
  const navigate = useNavigate()
  const [notifications, setNotifications] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const [processing, setProcessing] = useState(null)
  const [filter, setFilter] = useState("all")

  // États pour le popup de confirmation
  const [showDeletePopup, setShowDeletePopup] = useState(false)
  const [notificationToDelete, setNotificationToDelete] = useState(null)
  const [deleting, setDeleting] = useState(false)
  const [showDeleteAllPopup, setShowDeleteAllPopup] = useState(false)

  const getToken = () => localStorage.getItem("access_token")

  const deduplicateNotifications = (notifs) => {
    const uniqueMap = new Map()
    for (const notif of notifs) {
      if (!uniqueMap.has(notif.id)) {
        uniqueMap.set(notif.id, notif)
      }
    }
    return Array.from(uniqueMap.values())
  }

  const fetchNotifications = useCallback(async () => {
    try {
      const token = getToken()

      if (!token) {
        setError("Vous devez être connecté")
        setLoading(false)
        return
      }

      const response = await fetch(`${API_URL}/notifications/`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      if (!response.ok) throw new Error("Erreur de chargement")

      const data = await response.json()

      let notifs = []
      if (Array.isArray(data)) {
        notifs = data
      } else if (data.results && Array.isArray(data.results)) {
        notifs = data.results
      } else if (data.data && Array.isArray(data.data)) {
        notifs = data.data
      }

      const uniqueNotifs = deduplicateNotifications(notifs)
      uniqueNotifs.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
      
      setNotifications(uniqueNotifs)
    } catch (err) {
      console.error("Erreur:", err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }, [])

  const fetchUnreadCount = useCallback(async () => {
    try {
      const token = getToken()
      
      if (!token) return
      
      const response = await fetch(`${API_URL}/notifications/unread-count/`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      
      if (response.ok) {
        const data = await response.json()
        window.dispatchEvent(new CustomEvent('notifications-updated', { 
          detail: { count: data.count || 0 } 
        }))
      }
    } catch (error) {
      console.error("Erreur compteur:", error)
    }
  }, [])

  useEffect(() => {
    fetchNotifications()
    
    const interval = setInterval(() => {
      fetchNotifications()
      fetchUnreadCount()
    }, 30000)
    
    return () => clearInterval(interval)
  }, [fetchNotifications, fetchUnreadCount])

  useEffect(() => {
    const handleRefresh = () => {
      fetchNotifications()
      fetchUnreadCount()
    }
    
    window.addEventListener('notifications-updated', handleRefresh)
    window.addEventListener('invitation-updated', handleRefresh)
    
    return () => {
      window.removeEventListener('notifications-updated', handleRefresh)
      window.removeEventListener('invitation-updated', handleRefresh)
    }
  }, [fetchNotifications, fetchUnreadCount])

  const handleMarkAsRead = async (notificationId) => {
    try {
      const token = getToken()
      const response = await fetch(`${API_URL}/notifications/${notificationId}/mark_read/`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      })

      if (response.ok) {
        setNotifications(prev =>
          prev.map(n => (n.id === notificationId ? { ...n, est_lu: true } : n))
        )
        
        fetchUnreadCount()
        window.dispatchEvent(new CustomEvent('notifications-updated'))
      } else {
        const errorData = await response.json()
        console.error("Erreur mark read:", errorData)
      }
    } catch (err) {
      console.error("Erreur:", err)
    }
  }

  const handleMarkAllAsRead = async () => {
    try {
      const token = getToken()

      const response = await fetch(`${API_URL}/notifications/mark_all_read/`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      })

      if (response.ok) {
        setNotifications(prev => prev.map(n => ({ ...n, est_lu: true })))
        fetchUnreadCount()
        window.dispatchEvent(new CustomEvent('notifications-updated'))
      } else {
        const errorData = await response.json()
        console.error("Erreur mark all read:", errorData)
      }
    } catch (err) {
      console.error("Erreur:", err)
    }
  }

  const handleDeleteClick = (notification) => {
    setNotificationToDelete(notification)
    setShowDeletePopup(true)
  }

  const confirmDelete = async () => {
    if (!notificationToDelete) return

    setDeleting(true)
    try {
      const token = getToken()
      const response = await fetch(`${API_URL}/notifications/${notificationToDelete.id}/`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      if (response.ok || response.status === 204) {
        setNotifications(prev => prev.filter(n => n.id !== notificationToDelete.id))
        setShowDeletePopup(false)
        setNotificationToDelete(null)
        fetchUnreadCount()
        window.dispatchEvent(new CustomEvent('notifications-updated'))
      } else {
        const errorData = await response.json()
        console.error("Erreur suppression:", errorData)
      }
    } catch (err) {
      console.error("Erreur:", err)
    } finally {
      setDeleting(false)
    }
  }

  const handleDeleteAllClick = () => {
    setShowDeleteAllPopup(true)
  }

  const confirmDeleteAll = async () => {
    setDeleting(true)
    try {
      const token = getToken()

      const response = await fetch(`${API_URL}/notifications/clear_all/`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      })

      if (response.ok) {
        setNotifications([])
        setShowDeleteAllPopup(false)
        fetchUnreadCount()
        window.dispatchEvent(new CustomEvent('notifications-updated'))
      } else {
        const errorData = await response.json()
        console.error("Erreur clear all:", errorData)
      }
    } catch (err) {
      console.error("Erreur:", err)
    } finally {
      setDeleting(false)
    }
  }

  const handleInvitationResponse = async (notificationId, reponse) => {
    setProcessing(notificationId)
    try {
      const token = getToken()
      const response = await fetch(`${API_URL}/notifications/${notificationId}/repondre_invitation/`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ reponse }),
      })

      if (response.ok) {
        setNotifications(prev =>
          prev.map(n =>
            n.id === notificationId
              ? { ...n, est_lu: true, action_requise: false }
              : n
          )
        )

        window.dispatchEvent(new CustomEvent('invitation-updated', {
          detail: { type: reponse }
        }))
        
        setTimeout(() => {
          fetchNotifications()
          fetchUnreadCount()
        }, 500)
      } else {
        const errorData = await response.json()
        console.error("Erreur réponse invitation:", errorData)
      }
    } catch (err) {
      console.error("Erreur:", err)
    } finally {
      setProcessing(null)
    }
  }

  const handleNotificationClick = (notification) => {
    if (notification.lien) {
      navigate(notification.lien)
    }
  }

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'invitation_reunion':
        return <FaCalendarAlt style={{ color: "#5e17eb" }} size={18} />
      case 'reunion':
        return <FaCalendarAlt style={{ color: "#5e17eb" }} size={18} />
      case 'reunion_validee':
        return <FaCheck style={{ color: "#16a34a" }} size={18} />
      case 'reunion_rejetee':
        return <FaTimes style={{ color: "#ef4444" }} size={18} />
      case 'tache':
        return <FaTasks style={{ color: "#16a34a" }} size={18} />
      case 'compte_rendu':
        return <FaClipboardList style={{ color: "#2563eb" }} size={18} />
      case 'reponse_invitation':
        return <FaCheck style={{ color: "#16a34a" }} size={18} />
      case 'validation_requise':
        return <FaSpinner style={{ color: "#eab308" }} size={18} className="animate-spin" />
      default:
        return <FiBell style={{ color: "#666" }} size={18} />
    }
  }

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffMs = now - date
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)

    if (diffMins < 1) return "À l'instant"
    if (diffMins < 60) return `Il y a ${diffMins} min`
    if (diffHours < 24) return `Il y a ${diffHours} h`
    if (diffDays === 1) return "Hier"
    return `Il y a ${diffDays} jours`
  }

  const unreadCount = notifications.filter(n => !n.est_lu).length

  const filteredNotifications = notifications.filter(n => {
    if (filter === "unread") return !n.est_lu
    if (filter === "read") return n.est_lu
    return true
  })

  if (loading) {
    return (
      <Layout>
        <div style={styles.loadingContainer}>
          <div style={styles.spinner}></div>
          <p>Chargement des notifications...</p>
        </div>
      </Layout>
    )
  }

  return (
    <Layout>
      <div style={styles.page}>
        <div style={styles.header}>
          <div>
            <h1 style={styles.title}>NOTIFICATIONS</h1>
            <p style={styles.subtitle}>
              Gérez toutes vos notifications et invitations
            </p>
          </div>
        </div>

        {/* Stats Bar */}
        <div style={styles.statsBar}>
          <div style={styles.statItem}>
            <span style={styles.statValue}>{notifications.length}</span>
            <span style={styles.statLabel}>Total</span>
          </div>
          <div style={styles.statItem}>
            <span style={styles.statValue}>{unreadCount}</span>
            <span style={styles.statLabel}>Non lues</span>
          </div>
          <div style={styles.statItem}>
            <span style={styles.statValue}>{notifications.length - unreadCount}</span>
            <span style={styles.statLabel}>Lues</span>
          </div>
        </div>

        {/* Top Bar */}
        <div style={styles.topBar}>
          <div style={styles.filterGroup}>
            <button
              onClick={() => setFilter("all")}
              style={{
                ...styles.filterButton,
                backgroundColor: filter === "all" ? "#5e17eb" : "white",
                color: filter === "all" ? "white" : "#666",
              }}
            >
              Toutes ({notifications.length})
            </button>
            <button
              onClick={() => setFilter("unread")}
              style={{
                ...styles.filterButton,
                backgroundColor: filter === "unread" ? "#5e17eb" : "white",
                color: filter === "unread" ? "white" : "#666",
              }}
            >
              Non lues ({unreadCount})
            </button>
            <button
              onClick={() => setFilter("read")}
              style={{
                ...styles.filterButton,
                backgroundColor: filter === "read" ? "#5e17eb" : "white",
                color: filter === "read" ? "white" : "#666",
              }}
            >
              Lues ({notifications.length - unreadCount})
            </button>
          </div>

          <div style={styles.actionGroup}>
            <button onClick={handleMarkAllAsRead} style={styles.actionButton}>
              <FaCheckDouble size={14} /> Tout marquer lu
            </button>
            <button onClick={handleDeleteAllClick} style={{ ...styles.actionButton, color: "#ef4444" }}>
              <FaTrash size={14} /> Tout supprimer
            </button>
          </div>
        </div>

        {/* Notifications List */}
        <div style={styles.notificationsList}>
          {filteredNotifications.length === 0 ? (
            <div style={styles.emptyState}>
              <div style={styles.emptyIcon}>
                <FiBell size={48} color="#9ca3af" />
              </div>
              <p>Aucune notification</p>
              <p style={{ fontSize: 12, color: "#9ca3af" }}>
                Les notifications apparaîtront ici lorsqu'elles seront disponibles
              </p>
            </div>
          ) : (
            filteredNotifications.map((notification) => (
              <div
                key={notification.id}
                style={{
                  ...styles.notificationCard,
                  borderLeft: notification.est_lu
                    ? "4px solid transparent"
                    : "4px solid #5e17eb",
                  backgroundColor: !notification.est_lu ? "#f8f7ff" : "white",
                }}
              >
                <div style={styles.notificationContent}>
                  <div style={styles.notificationIcon}>
                    {getNotificationIcon(notification.type)}
                  </div>

                  <div style={styles.notificationMain}>
                    <div style={styles.notificationHeader}>
                      <h4 style={{
                        ...styles.notificationTitle,
                        fontWeight: !notification.est_lu ? 600 : 500,
                      }}>
                        {notification.titre}
                      </h4>
                      <span style={styles.notificationTime}>
                        {formatTimeAgo(notification.created_at)}
                      </span>
                    </div>

                    <p style={styles.notificationMessage}>{notification.message}</p>

                    {notification.action_requise && notification.type === 'invitation_reunion' && (
                      <div style={styles.invitationActions}>
                        <button
                          onClick={() => handleInvitationResponse(notification.id, 'accepte')}
                          disabled={processing === notification.id}
                          style={styles.acceptButton}
                        >
                          {processing === notification.id ? (
                            <div style={styles.smallSpinner}></div>
                          ) : (
                            <>
                              <FiCheck size={14} /> Accepter
                            </>
                          )}
                        </button>
                        <button
                          onClick={() => handleInvitationResponse(notification.id, 'refuse')}
                          disabled={processing === notification.id}
                          style={styles.refuseButton}
                        >
                          {processing === notification.id ? (
                            <div style={styles.smallSpinner}></div>
                          ) : (
                            <>
                              <FiX size={14} /> Refuser
                            </>
                          )}
                        </button>
                      </div>
                    )}
                  </div>

                  <div style={styles.notificationActions}>
                    {!notification.est_lu && (
                      <button
                        onClick={() => handleMarkAsRead(notification.id)}
                        style={styles.markReadButton}
                        title="Marquer comme lue"
                      >
                        <FaCheck size={12} />
                      </button>
                    )}
                    <button
                      onClick={() => handleDeleteClick(notification)}
                      style={styles.deleteButton}
                      title="Supprimer"
                    >
                      <FaTrash size={12} />
                    </button>
                  </div>
                </div>

                {notification.lien && (
                  <button
                    onClick={() => handleNotificationClick(notification)}
                    style={styles.viewLink}
                  >
                    Voir les détails →
                  </button>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      {/* Popup de confirmation pour suppression unique */}
      {showDeletePopup && notificationToDelete && (
        <div style={styles.overlay}>
          <div style={styles.deletePopup}>
            <div style={styles.deleteIcon}>
              <FiAlertCircle size={40} color="#ef4444" />
            </div>
            <h3 style={styles.deleteTitle}>Confirmer la suppression</h3>
            <p style={styles.deleteMessage}>
              Êtes-vous sûr de vouloir supprimer définitivement cette notification ?
            </p>
            <p style={styles.deleteWarning}>
              Cette action est irréversible.
            </p>
            <div style={styles.deleteActions}>
              <button
                onClick={() => setShowDeletePopup(false)}
                style={styles.deleteCancelButton}
                disabled={deleting}
              >
                Annuler
              </button>
              <button
                onClick={confirmDelete}
                style={styles.deleteConfirmButton}
                disabled={deleting}
              >
                {deleting ? 'Suppression...' : 'Oui, supprimer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Popup de confirmation pour suppression de toutes les notifications */}
      {showDeleteAllPopup && (
        <div style={styles.overlay}>
          <div style={styles.deletePopup}>
            <div style={styles.deleteIcon}>
              <FiAlertCircle size={40} color="#ef4444" />
            </div>
            <h3 style={styles.deleteTitle}>Confirmer la suppression</h3>
            <p style={styles.deleteMessage}>
              Êtes-vous sûr de vouloir supprimer <strong>toutes</strong> vos notifications ?
            </p>
            <p style={styles.deleteWarning}>
              Cette action est irréversible.
            </p>
            <div style={styles.deleteActions}>
              <button
                onClick={() => setShowDeleteAllPopup(false)}
                style={styles.deleteCancelButton}
                disabled={deleting}
              >
                Annuler
              </button>
              <button
                onClick={confirmDeleteAll}
                style={styles.deleteConfirmButton}
                disabled={deleting}
              >
                {deleting ? 'Suppression...' : 'Oui, tout supprimer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  )
}

const styles = {
  page: {
    background: "#f7f6ff",
    padding: "22px",
    borderRadius: "16px",
    minHeight: "100vh",
  },
  header: {
    marginBottom: "20px",
  },
  title: {
    fontSize: "26px",
    color: "#1f1f2e",
    marginBottom: "4px",
    fontWeight: "500",
  },
  subtitle: {
    color: "#666",
    fontSize: "14px",
  },
  loadingContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "400px",
    gap: "16px",
  },
  spinner: {
    width: "40px",
    height: "40px",
    border: "3px solid #f3f3f3",
    borderTop: "3px solid #5e17eb",
    borderRadius: "50%",
    animation: "spin 1s linear infinite",
  },
  smallSpinner: {
    width: "12px",
    height: "12px",
    border: "2px solid white",
    borderTop: "2px solid transparent",
    borderRadius: "50%",
    animation: "spin 1s linear infinite",
  },
  statsBar: {
    display: "flex",
    gap: "32px",
    background: "white",
    padding: "16px 24px",
    borderRadius: "12px",
    marginBottom: "20px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
    flexWrap: "wrap",
  },
  statItem: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    minWidth: "80px",
  },
  statValue: {
    fontSize: "28px",
    fontWeight: "bold",
    color: "#1f1f2e",
  },
  statLabel: {
    fontSize: "12px",
    color: "#666",
    marginTop: "4px",
  },
  topBar: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "20px",
    flexWrap: "wrap",
    gap: "12px",
  },
  filterGroup: {
    display: "flex",
    gap: "8px",
    background: "white",
    padding: "4px",
    borderRadius: "30px",
    border: "1px solid #e5e7eb",
  },
  filterButton: {
    padding: "6px 16px",
    borderRadius: "30px",
    fontSize: "13px",
    fontWeight: "500",
    cursor: "pointer",
    transition: "all 0.2s",
    border: "none",
  },
  actionGroup: {
    display: "flex",
    gap: "8px",
  },
  actionButton: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    padding: "8px 16px",
    background: "white",
    border: "1px solid #e5e7eb",
    borderRadius: "8px",
    color: "#5e17eb",
    fontSize: "13px",
    fontWeight: "500",
    cursor: "pointer",
    transition: "all 0.2s",
  },
  notificationsList: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },
  emptyState: {
    textAlign: "center",
    padding: "60px",
    background: "white",
    borderRadius: "16px",
    color: "#6b7280",
  },
  emptyIcon: {
    marginBottom: "16px",
  },
  notificationCard: {
    background: "white",
    borderRadius: "12px",
    padding: "16px",
    boxShadow: "0 2px 6px rgba(0,0,0,0.04)",
    transition: "all 0.2s",
    border: "1px solid #e5e7eb",
  },
  notificationContent: {
    display: "flex",
    gap: "14px",
    alignItems: "flex-start",
  },
  notificationIcon: {
    width: "40px",
    height: "40px",
    borderRadius: "50%",
    background: "#f3f4f6",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  notificationMain: {
    flex: 1,
  },
  notificationHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "6px",
    flexWrap: "wrap",
    gap: "6px",
  },
  notificationTitle: {
    fontSize: "15px",
    color: "#1f1f2e",
    margin: 0,
  },
  notificationTime: {
    fontSize: "11px",
    color: "#9ca3af",
  },
  notificationMessage: {
    fontSize: "13px",
    color: "#4b5563",
    lineHeight: "1.5",
    margin: 0,
  },
  invitationActions: {
    display: "flex",
    gap: "10px",
    marginTop: "12px",
  },
  acceptButton: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    padding: "6px 14px",
    background: "#22c55e",
    color: "white",
    border: "none",
    borderRadius: "6px",
    fontSize: "12px",
    fontWeight: "500",
    cursor: "pointer",
    transition: "all 0.2s",
  },
  refuseButton: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    padding: "6px 14px",
    background: "#ef4444",
    color: "white",
    border: "none",
    borderRadius: "6px",
    fontSize: "12px",
    fontWeight: "500",
    cursor: "pointer",
    transition: "all 0.2s",
  },
  notificationActions: {
    display: "flex",
    gap: "8px",
    alignItems: "center",
    flexShrink: 0,
  },
  markReadButton: {
    width: "32px",
    height: "32px",
    borderRadius: "8px",
    background: "#ede9ff",
    color: "#5e17eb",
    border: "none",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "all 0.2s",
  },
  deleteButton: {
    width: "32px",
    height: "32px",
    borderRadius: "8px",
    background: "#fee2e2",
    color: "#ef4444",
    border: "none",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "all 0.2s",
  },
  viewLink: {
    marginTop: "12px",
    paddingTop: "8px",
    color: "#5e17eb",
    fontSize: "12px",
    fontWeight: "500",
    background: "transparent",
    border: "none",
    cursor: "pointer",
    textAlign: "left",
    borderTop: "1px solid #e5e7eb",
    width: "100%",
    display: "flex",
    alignItems: "center",
    gap: "4px",
    transition: "all 0.2s",
  },
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    backdropFilter: 'blur(4px)',
  },
  deletePopup: {
    background: 'white',
    borderRadius: 16,
    padding: 32,
    width: '90%',
    maxWidth: 450,
    textAlign: 'center',
  },
  deleteIcon: {
    marginBottom: 16,
  },
  deleteTitle: {
    fontSize: 20,
    fontWeight: 600,
    color: '#1f1f2e',
    marginBottom: 12,
  },
  deleteMessage: {
    fontSize: 14,
    color: '#4b5563',
    marginBottom: 8,
    lineHeight: 1.5,
  },
  deleteWarning: {
    fontSize: 13,
    color: '#ef4444',
    background: '#fee2e2',
    padding: 10,
    borderRadius: 8,
    marginBottom: 20,
  },
  deleteActions: {
    display: 'flex',
    justifyContent: 'center',
    gap: 12,
  },
  deleteCancelButton: {
    padding: '10px 20px',
    background: '#f3f4f6',
    border: 'none',
    borderRadius: 8,
    fontSize: 14,
    color: '#666',
    cursor: 'pointer',
  },
  deleteConfirmButton: {
    padding: '10px 20px',
    background: '#ef4444',
    border: 'none',
    borderRadius: 8,
    fontSize: 14,
    color: 'white',
    cursor: 'pointer',
  },
}

const styleSheet = document.createElement("style")
styleSheet.textContent = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`
document.head.appendChild(styleSheet)